import i18nIsoCountries from "i18n-iso-countries";

import filter from "lodash/filter";
import uniq from "lodash/uniq";
import last from "lodash/last";
import isEmpty from "lodash/isEmpty";
import kebabCase from "lodash/kebabCase";
import forOwn from "lodash/forOwn";
import get from "lodash/get";
import values from "lodash/values";

import settings from "./settings";

/**
 * This function gets the browser url and returns an object
 * containing all the given url parameters in a key-value
 * pair format. For ex. ${baseUrl}/?client=demo&theme=demo returns
 * {client:"demo",theme:"demo"}
 * @return {Object}  Τhe url params in key-value pair format .
 */
export const getUrlParams = () => {
  const url = window.location.search;
  const params = new URLSearchParams(url);
  const urlParamsObject = {};
  for (const [key, value] of params) {
    // each 'entry' is a [key, value] tupple
    urlParamsObject[key] = value;
  }
  return urlParamsObject;
};

/**
 * Returns client name and api auth token. These props should exist in vaix
 * window settings object when the widgets are embedded in the client's site.
 * But the app should also work as a preview on widgets builder admin UI and
 * as a demo site as well. In these cases, the client props should exist in
 * url params, otherwise the demo site props are returned.
 * @return {String}  The client name.
 */
export const getClientProps = () => {
  const vaixCustomerSettings = window?.vaixCustomerSettings;
  let clientName, apiAuthToken;

  if (vaixCustomerSettings) {
    clientName = vaixCustomerSettings.operatorName;
    apiAuthToken = vaixCustomerSettings.apiToken;
  } else {
    const { operatorName, apiToken } = getUrlParams();
    clientName = operatorName || "demo";
    apiAuthToken =
      apiToken ||
      "e55d777f29821a0166010d481693699722e965734935dfc53e2d677c690b00fe";
    // "8d3f45054413bd401f9512ae51d9e0f4d9591cbb1a48edda5c854d7b3a8cfea1";
  }

  return { clientName, apiAuthToken };
};

/**
 * This function gets the operator id from the window settings
 * and returns the operator name from the available operators using that id.
 * @return {String}  The operator name.
 */
// export const getOperatorFromWindowSettings = () => {
//   const AVAILABLE_OPERATORS = {
//     "8d3f45054413bd401f9512ae51d9e0f4d9591cbb1a48edda5c854d7b3a8cfea1": "demo",
//     c234c24eb22e8671ad212614c9c511f3886c8b68eca586ebccaa81dfd467f6fe:
//       "grandCasinoBern",
//   };

//   const operatorId = getApiAuthToken();
//   const operator = AVAILABLE_OPERATORS[operatorId] ?? "demo";

//   return operator;
// };

/**
 * This function detects whether the user is using a mobile device. It uses a
 * regExp to check if the user agent of the browser is a mobile agent and returns
 * true if there is a match, otherwise false.
 * @return {Boolean} Whether the user is using a mobile device or not.
 */
export const isMobile = () => {
  // found at https://stackoverflow.com/questions/11381673/detecting-a-mobile-browser
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
};

const checkStatus = function (response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  let error = new Error(response.statusText);
  error.response = response;
  throw error;
};

export const fetchData = function (url, headers) {
  return fetch(url, { headers, mode: "cors" })
    .then(checkStatus)
    .then((response) => {
      return response.json();
    })
    .catch((error) => {
      console.debug("Error fetching data");
      console.trace(error);
      throw error;
    });
};

/**
 * Returns the amount of items per row on a slider depending on the screen size.
 * A slider will show the maximum amount of items (maxItemsPerRow param) if they
 * fit in the screen, otherwise it will show the appropriate amount for the
 * given screen size.
 * @param  {Integer} screenWidth    The maximun amount of items to show in a row.
 * @param  {Integer} maxItemsPerRow The width of the screen in px.
 * @return {Integer}                The amount of items for each row.
 */
export const getItemsPerRow = function (screenWidth, maxItemsPerRow) {
  let itemsPerRow = maxItemsPerRow;

  const operatorSettings = settings?.slider?.itemsPerRow;

  if (screenWidth < 500) {
    itemsPerRow = operatorSettings?.["500px"] || 2;
  } else if (screenWidth < 800) {
    itemsPerRow = operatorSettings?.["800px"] || 3;
  } else if (screenWidth < 1080) {
    itemsPerRow = operatorSettings?.["1080px"] || 4;
  } else if (screenWidth < 1300) {
    itemsPerRow = operatorSettings?.["1300px"] || 5;
  }

  //make items a float in mobile to include in the view a part of the slider that is not normally visible
  let computedItems = Math.min(itemsPerRow, maxItemsPerRow);

  return isMobile() ? computedItems + 0.2 : computedItems;
};

/**
 * A function that checks if an array has empty entries, false entries or the string "na"
 * and returns the array without these values.
 * @param   {Array} results The list of values to be checked
 * @returns {Array}         The array given as param without falsy, empty or "na" entries
 */
export const excludeEmptyResults = function (results) {
  return filter(results, (value) => {
    return (value || value === 0) && value !== "na";
  });
};

export const arrayEquals = (a, b) => {
  return (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((val, index) => val === b[index])
  );
};

/**
 * Adds the specified filters to the current filters and returns the updated result.
 * We first check if the key of the filter to add already exists in the current filters
 * and if it does we combine their values,else we append the filter.
 * @param {string} filtersStr - The default filters to which the new filters will be added.
 * @param {Object[]} newFiltersProps - The filters to be added where each object has three properties "field","operator" and "filterValue".
 * @returns {string} - The updated filters after adding the new filters.
 * @example
 * let filtersStr = "game_studio:in:Netent,PragmaticBingo;vaix_tags:in:Christmas"
 * let newFiltersProps = [{field: "game_studio", operator: "in", filterValue: "Pragmatic"}]
 * Output :"game_studio:in:Pragmatic,Netent,PragmaticBingo;vaix_tags:in:Christmas"
 */
export const concatFilters = (filtersStr = "", newFiltersProps = []) => {
  const filters = filtersStr.split(";");

  newFiltersProps.forEach(({ field, operator, filterValue }) => {
    const filterMatch = filters.filter((f) => f.includes(field))[0];

    const value = isEmpty(filterMatch)
      ? filterValue
      : uniq(last(filterMatch.split(":")).concat(",", filterValue));
    filters.push(`${field}:${operator}:${value}`);
  });

  let computedFilters = excludeEmptyResults(filters);

  return computedFilters.join(";");
};

/**
 * A function that takes the theme for a component and unwraps it's attributes
 * one by one creating a string containing all the unwrapped attributes.
 * @param   {Object} styles The css properties of a component
 * @returns {string}        The string containing the list of css properties of the component
 */
export const unwrapStyles = (styles) => {
  let unwrappedComponent = "";
  forOwn(styles, (value, key) => {
    unwrappedComponent += `${kebabCase(key)}: ${value};`;
  });

  return unwrappedComponent;
};

/**
 * Rounds number to fixed decimal places.
 *
 * @param {number} number - The number to round.
 * @param {number} precision - The number of digits of the decimal part we want to keep.
 * @returns {number} - The round number.
 */
export function roundTo(number, precision) {
  return parseFloat(number.toFixed(precision));
}

/**
 * Checks if a string contains any substrings provided in the the keywords array.
 * @param   {String} str      The string to check.
 * @param   {Array}  keywords The substrings to be found in the string provided.
 * @returns {Boolean}         Whether the string contains any of the given substrings or not.
 */
export const includesKeywords = (str, keywords = []) =>
  keywords.some((key) => str.toUpperCase().includes(key));

/**
 * Checks if the current browser host is a vaix domain.
 * @returns {boolean} True if current host is in list of vaix hosts.
 */
export const isVaixHost = () => {
  const currentHost = window?.location?.hostname;

  const vaixHosts = [
    "casino-widgets-demo.vaix.ai",
    "casino-widgets.vaix.ai",
    // "localhost",
  ];

  return vaixHosts.some((h) => h === currentHost);
};

/**
 * Capitalizes the first letter of every word in a string
 * It doen't affect special charactes
 * or other capital letters that already exist in the string.
 * @param {String} title The string that should capitalized
 * @return {String}     The capitalized string
 */
export const capitalizeTitle = (title) => {
  return title
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Get the image source URL for the target device type (mobile or desktop) from a list of image objects.
 * @param {Array} imageList - An array of image objects, each containing a 'type' and 'url' property.
 * @returns {string} - The URL of the image corresponding to the target device type, or an empty string if not found.
 */
export const getDeviceImageSrc = (imageList = []) => {
  const targetDevice = isMobile() ? "mobile" : "desktop";

  const targetElem = imageList.find((i) => i?.type === targetDevice);
  const imageSrc = get(targetElem, "url");

  return imageSrc;
};

/**
 * Retrieves country translations for a given locale and returns them in the object format react-i18next accepts.
 * @param {string} locale - The locale for which country translations are needed.
 * @returns {Object} - An object containing country translations for the specified locale.
 */
export const getCountryTranslations = (locale) => {
  const availableLanguages = i18nIsoCountries.getSupportedLanguages();

  const countryTranslations = {};

  i18nIsoCountries.registerLocale(
    require(`i18n-iso-countries/langs/${locale}.json`)
  );

  availableLanguages.forEach((language) => {
    countryTranslations[language] = { translation: {} };

    const allCountryCodes = values(i18nIsoCountries.getAlpha3Codes());
    allCountryCodes.forEach((countryCode) => {
      const countryName = i18nIsoCountries.getName(countryCode, locale);
      countryTranslations[language].translation[countryCode] = countryName;
    });
  });

  return countryTranslations;
};

/**
 * Detects the channel/platform from which the user is accessing the application.
 * Analyzes the user agent string to determine the specific operating system or device type.
 *
 * @returns {string} The detected channel/platform. Possible values:
 *   - 'ios' - for iPad, iPhone, or iPod devices
 *   - 'android' - for Android devices
 *   - 'mac' - for macOS/Mac OS X systems
 *   - 'windows' - for Windows systems
 *   - 'linux' - for Linux systems
 *   - 'mobile' - fallback for unidentified mobile devices
 *   - 'desktop' - fallback for unidentified desktop devices
 *
 * @example
 * // On an iPhone
 * detectChannel(); // returns 'ios'
 *
 * @example
 * // On a Mac desktop
 * detectChannel(); // returns 'mac'
 *
 * @example
 * // On an Android phone
 * detectChannel(); // returns 'android'
 */
export const detectChannel = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Check specific platforms
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return "ios";
  }

  if (/android/i.test(userAgent)) {
    return "android";
  }

  if (/Macintosh|Mac OS X/.test(userAgent)) {
    return "mac";
  }

  if (/Windows/.test(userAgent)) {
    return "windows";
  }

  if (/Linux/.test(userAgent)) {
    return "linux";
  }

  // Fallback to desktop if not mobile and platform not detected
  return isMobile() ? "mobile" : "desktop";
};
